import { useEffect, memo, useState } from 'react';
import { Avatar, Box, List, ListItemButton, ListItemIcon, ListItemText, Paper, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { useLocales } from 'src/locales';
import countriesJson from 'src/utils/countries.json';
import Iconify from 'src/components/iconify';
import { DomainItem, AdType } from '../types';
import { domainFavicon } from '../utils';
import { normalizeDomain } from '../utils/domain';
import HoverShortcut from './HoverShortcut';

interface Props {
    items: DomainItem[];
    selected: string;
    onSelect: (domain: string) => void;
    adType: AdType;
    selectedCountry?: string | null;
}

function LeftDomainsBase({ items, selected, onSelect, selectedCountry }: Props) {
    const workspace: any = useCurrentWorkspace();
    const { t } = useLocales();
    const navigate = useNavigate();

    const friendlyDomainNames: string[] = Array.from(new Set([
        ...(Array.isArray(workspace?.friendlyDomains) ? (workspace.friendlyDomains as string[]).filter(Boolean) : []),
        ...(workspace?.url ? [workspace.url] : [])
    ]
        .map((d: string) => normalizeDomain(d))
        .filter(Boolean)));

    const isFriendly = (domain: string) => friendlyDomainNames.includes(normalizeDomain(domain));

    const yourDomains: DomainItem[] = items.filter((it) => isFriendly(it.domain));
    const competitorDomains: DomainItem[] = items.filter((it) => !isFriendly(it.domain));

    const firstItemDomain = (yourDomains[0]?.domain || competitorDomains[0]?.domain || '').toString();
    useEffect(() => {
        if (!selected && firstItemDomain) {
            onSelect(firstItemDomain);
        }
    }, [selected, firstItemDomain, onSelect]);

    const handleEvolutionClick = (e: React.MouseEvent, domain: string) => {
        e.stopPropagation();
        const countryEntry = countriesJson?.find((c) => c.name === selectedCountry);
        const code = countryEntry?.code?.toUpperCase() || '';
        navigate(`/competitive-intelligence/competition-evolution?country=${encodeURIComponent(code)}&metric=paid&domain=${encodeURIComponent(domain)}`);
    };

    const hoverLabel = (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
            See Evolution
            <Iconify icon="eva:diagonal-arrow-right-up-fill" width={14} />
        </Box>
    );

    const DomainRow = ({ it }: { it: DomainItem }) => {
        const [rowHovered, setRowHovered] = useState(false);
        const paidPresenceRate = it.paidPresenceRate || 0;
        return (
            <ListItemButton
                onMouseEnter={() => setRowHovered(true)}
                onMouseLeave={() => setRowHovered(false)}
                onClick={() => onSelect(it.domain)}
                selected={selected === it.domain}
                sx={{
                    position: 'relative',
                    height: "40px",
                    py: "10px",
                    borderRadius: 1,
                    mb: 0.5,
                    overflow: 'hidden',
                    '&.Mui-selected': { bgcolor: 'action.selected' },
                    '&.Mui-selected:hover': { bgcolor: 'action.selected' },
                }}
            >
                <ListItemIcon sx={{ minWidth: 28 }}>
                    <Avatar
                        src={domainFavicon(it.domain)}
                        sx={{ width: 18, height: 18, backgroundColor: '#fff', transition: 'opacity 0.3s ease-in-out' }}
                    />
                </ListItemIcon>
                <ListItemText
                    primary={
                        <Typography variant="body2">{it.domain}</Typography>
                    }
                />
                <Typography variant="body2" sx={{ opacity: 0.8, flexShrink: 0 }}>
                    {paidPresenceRate.toFixed(0)}%
                </Typography>
                {rowHovered && (
                    <Box
                        sx={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            pl: 10,
                            pr: 1,
                            background: 'linear-gradient(to right, rgba(255,255,255,0) 0%, rgba(255,255,255,0.85) 40%)',
                        }}
                    >
                        <HoverShortcut
                            defaultContent=""
                            hoverContent={hoverLabel}
                            onClick={(e) => handleEvolutionClick(e, it.domain)}
                            forceHovered
                            fontSize={14}
                            sx={{ ml: 0 }}
                        />
                    </Box>
                )}
            </ListItemButton>
        );
    };

    const renderRow = (it: DomainItem) => <DomainRow key={it.domain} it={it} />;

    return (
        <Paper sx={{ p: 1.5, backgroundColor: 'transparent', borderRadius: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
            {yourDomains.length > 0 && (
                <>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                        <Typography
                            variant="overline"
                            sx={{ color: 'text.secondary', fontSize: 12, fontWeight: 400 }}>
                            {t("dashboard.global.competitiveIntelligence.your_domains")}
                        </Typography>
                        <Typography variant="overline" sx={{ color: 'text.secondary', fontSize: 12, fontWeight: 400 }}>Paid presence rate</Typography>
                    </Stack>
                    <List dense disablePadding sx={{ mb: 1, mt: 1 }}>
                        {yourDomains.map(renderRow)}
                    </List>
                </>
            )}

            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ pt: 1.5, mb: 1, mt: yourDomains.length > 0 ? 0.5 : 0 }}>
                <Typography
                    variant="overline"
                    sx={{ color: 'text.secondary', fontSize: 12, fontWeight: 400 }}>
                    {t("dashboard.global.competitiveIntelligence.competitors")} 
                </Typography>
                <Typography variant="overline" sx={{ color: 'text.secondary', fontSize: 12, fontWeight: 400 }}>Paid presence rate</Typography>
            </Stack>
            <List dense disablePadding sx={{ flex: 1 }}>
                {competitorDomains.map(renderRow)}
            </List>
        </Paper>
    );
}

const LeftDomains = memo(LeftDomainsBase, (prevProps, nextProps) =>
    prevProps.items === nextProps.items &&
    prevProps.selected === nextProps.selected &&
    prevProps.adType === nextProps.adType &&
    prevProps.selectedCountry === nextProps.selectedCountry
);

export default LeftDomains;


